<template>

<!--
    Job Autocomplete Component
    
    @version 1.0
-->

<div class="input-group w-100 flex-nowrap border-0">

    <!-- job auto complete -->
    <kt-autocomplete
        class="flex-grow-1"
        inputclass="form-control rounded-end-0 form-control-sm"
        :options="filteredList"
        rowvalue="RequestDetailedDescription"
        :rowkey="props.rowkey"
        :label="props.label"
        :placeholder="props.placeholder"
        @change="onChange"
        v-model="model"
        suffix-class="p-0"
        :min-width="350"
    >
      
    <!-- drop down template -->
    <template #option="{ option }">
      <slot name="option" :option="option">
        <div class="dropdown-item autocomplete-option">
            {{ customJobLabel(option) }}
        </div>
      </slot>
    </template>

    <template #suffix>

        <!-- filter jobs -->
        <kt-multi-select
            css="rounded-0"
            placeholder="<i class='fa fa-filter'></i>"
            :options="statusOptions"
            labelKey="Status"
            valueKey="Id"
            v-model="statusFilter"
        >
        </kt-multi-select>

        <!-- edit job button -->
        <button 
            v-if="model"
            @click.stop.prevent="editJob(model)" 
            class="btn btn-sm btn-outline-secondary rounded-start-0 rounded-end-0"
            type="button"
        ><i class="fa fa-edit"></i>
        </button>

        <!-- new job button -->
        <button 
            @click.stop.prevent="editJob()"
            class="btn btn-sm btn-outline-primary rounded-start-0"
            type="button"
        ><i class="fa fa-plus"></i>
        </button>
    </template>

    </kt-autocomplete>

</div>

<kt-modal
    :title="formTitle"
    :visible="formVisible"
    :size="'modal-xl'"
    @dismiss="formDismiss()"
>
    <template #body>
        <pm-form
            v-if="isPM"
            v-model="pmForm"
        ></pm-form>
        <job-form
            v-else
            v-model="form"
        ></job-form>
    </template>

    <template #footer>
        <div class="-container-fluid -px-0 kt-modal-actions">
            <button 
                @click="formDismiss()" 
                class="btn btn-outline-secondary -form-control"
            ><X :size="16" class="me-1" />Close</button>
            <button 
                @click="formSave(form)" 
                class="btn btn-outline-primary -form-control"
            ><Save :size="16" class="me-1" />Save</button>
        </div>
    </template>
</kt-modal>

</template>

<script setup>

import { ref, watch, onUnmounted, onMounted, computed } from "vue";
import { useJobStatusStore } from "@/stores/JobStatusStore";
import { storeToRefs } from "pinia";

import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import OptionsService from "@/services/OptionsService";
import { usePermissions } from "@/composables/usePermissions";
import KtAutocomplete from "@/components/UI/KtAutocomplete.vue";
import KtMultiSelect from "@/components/UI/KtMultiSelect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import JobForm from "@/components/Jobs/JobForm.vue";
import PmForm from "@/components/PMs/PMForm.vue";
import { X, Save } from 'lucide-vue-next';

const model = defineModel({
    type: String,
    default: "",
});

const props = defineProps({ 
    "rowkey": {
        type: String,
        default: "Id"
    },

    "label": {
        type: String,
        default: "Select job"
    },
    
    "client": {
        type: String,
        default: ""
    },
    
    "inputClass": {
        type: String,
        default: ""
    },
    
    "placeholder": {
        type: String,
        default: "Choose client then select a job"
    },
    
    "pmOnly": {
        type: Boolean,
        default: false
    },
    
    // deprecated ...
    "status": {
        type: Array,
        default: []
    },
});

const emits = defineEmits(["change", "status-change"]);
const jobStatusStore = useJobStatusStore();
const { userId } = usePermissions();
const JOBAC_FILTER_KEY = 'jobac_status_filter';

const { list: statusList } = storeToRefs(jobStatusStore);

// Convert the Record to an array for kt-multi-select
const statusOptions = computed(() => Object.values(statusList.value));

const statusFilter = ref([]);
let filterInitialized = false;
let statusWatchCount = 0;
let statusFilterWatchCount = 0;

// Normalize status prop to always be an array of numbers
const normalizeStatus = (status) => {
    if (!status) return [];
    if (Array.isArray(status)) {
        return status.map(s => Number(s));
    }
    // Handle single value (number or string)
    return [Number(status)];
};

// Sync statusFilter when status prop changes (only if not yet initialized from saved prefs)
watch(() => props.status, (newStatus) => {
    statusWatchCount++;
    if (statusWatchCount % 50 === 0) {
        console.warn(`⚠️ JobAc props.status watcher fired ${statusWatchCount} times! INFINITE LOOP!`, newStatus);
    }
    console.debug(`🔄 JobAc props.status watch (call #${statusWatchCount}):`, newStatus, 'filterInitialized:', filterInitialized);
    if (filterInitialized) return;
    const normalized = normalizeStatus(newStatus);
    if (normalized.length > 0) {
        statusFilter.value = normalized;
    }
}, { immediate: true });

const list = ref([]);
const form = ref({});
const pmForm = ref({});
const isPM = ref(false);
const formTitle = ref("New Job");
const formVisible = ref(false);
const isLoadingJobs = ref(false);

const unwatch = [];

const customJobLabel = (row) => {
    // format should be STATUS - JobId - Description
    let name = row.RequestDetailedDescription 
        || row.TaskDetail
        || "Description Not Set";

    for (let i = 0; i < jobStatusStore.list.length; i++) {
        let tmp = jobStatusStore.list[i];
        if (tmp.Id === row.RequestStatus) {
            name = tmp.Status + " - " + name;
            break;
        }
    }

    let id = row.Id;
    name = id + " | " + name;

    return name;
};

// Computed property to filter list based on status
const filteredList = computed(() => {
    if (!statusFilter.value || statusFilter.value.length === 0) {
        return list.value;
    }
    return list.value.filter(job => 
        statusFilter.value.includes(job.RequestStatus)
    );
});

const loadJobs = async () => {
    if (!props.client) {
        list.value = [];
        model.value = "";
        return;
    }

    // Set loading flag to prevent UI flickering
    isLoadingJobs.value = true;

    try {
        let status = undefined;
        
        if (props.status?.length) {
            status = (props.status + "").split(",");
        }

        if (statusFilter.value && statusFilter.value.length > 0) {
            status = statusFilter.value;
        }

        const jobs = await Api.loadJobs({ 
            CustomerNumber: props.client, 
            Size: 9999999,
            Status: status,
            OnlyPMs: props.pmOnly
        });
        
        // Only update list after jobs are loaded to prevent dropdown flicker
        list.value = jobs;
    } finally {
        isLoadingJobs.value = false;
    }
};

const onChange = (value) => {
    emits("change", value);
};

const editJob = async (val) => {
    if (val) {
        // Check if this request is PM-linked
        try {
            const pmData = await ApiService.loadPMByRequest(val);
            if (pmData && pmData.Id) {
                // This is a PM-linked request - show PM form
                isPM.value = true;
                pmForm.value = pmData;
                formTitle.value = "Edit PM";
                formVisible.value = 1;
                return;
            }
        } catch (error) {
            // Not a PM-linked request, continue with regular flow
            console.log("Not a PM-linked request");
        }
        
        // Regular request - show job form
        isPM.value = false;
        formTitle.value = "Edit Request";
        form.value = await ApiService.loadRequest(val);
    } else {
        // New request
        isPM.value = false;
        form.value = {};

        if (props.client) {
            formTitle.value = "New Request";
            form.value.Customer = props.client;
        }
    }

    formVisible.value = 1;
};

unwatch.push(watch(() => props.client, async (nv, ov) => {
    await loadJobs();
    // Job status store is already loaded in onMounted, no need to reload on every client change
}, {
    immediate: true
}));

unwatch.push(watch(() => statusFilter.value, async (nv, ov) => {
    statusFilterWatchCount++;
    if (statusFilterWatchCount % 50 === 0) {
        console.warn(`⚠️ JobAc statusFilter watcher fired ${statusFilterWatchCount} times! INFINITE LOOP!`, nv);
    }
    console.debug(`🔄 JobAc statusFilter watch (call #${statusFilterWatchCount}):`, nv, 'old:', ov);
    
    // Don't reload from API, just filter the existing list
    // The filteredList computed property handles this
    console.debug(`📡 JobAc emitting status-change:`, nv);
    emits("status-change", nv);

    // Persist filter per user
    if (filterInitialized && userId.value) {
        try {
            const val = nv && nv.length > 0 ? JSON.stringify(nv) : null;
            await OptionsService.set(JOBAC_FILTER_KEY, val, 'StaffId', String(userId.value));
        } catch (e) {
            console.warn('Failed to save JobAc filter preference', e);
        }
    }
}));

const formDismiss = () => {
    formVisible.value = 0;
};

const formSave = async (rec) => {
    if (isPM.value) {
        // Save as PM
        await ApiService.savePM(pmForm.value);
    } else {
        // Save as regular request
        await Api.saveJob(form.value);
    }
    await loadJobs();
    formVisible.value = 0;
};

onMounted(async () => {
    console.debug('🎬 JobAc onMounted START');
    await jobStatusStore.load();
    console.debug('✅ JobStatusStore loaded');

    // Load saved filter preference for this user
    if (userId.value) {
        try {
            const saved = await OptionsService.get(JOBAC_FILTER_KEY, 'StaffId', String(userId.value));
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    statusFilter.value = parsed.map(Number);
                }
            }
        } catch (e) {
            console.warn('Failed to load JobAc filter preference', e);
        }
    }
    filterInitialized = true;
    console.debug('✅ JobAc filter initialized, saved filter applied:', statusFilter.value);
    console.warn('🎬 JobAc onMounted END');
});

onUnmounted(() => {
    unwatch.forEach((val) => {
        val();
    });
});

</script>
